import { ChangeDetectorRef, Component, ElementRef, Inject, Input, NgZone, OnDestroy, OnInit, ViewChild, } from '@angular/core';

import { DOCUMENT } from '@angular/common';


import { AlertsService } from 'shared/alerts/alerts.service';
import { takeWhile } from 'rxjs/operators';
import { fromEvent } from 'rxjs/internal/observable/fromEvent';
import { Alert, ProcessStatus, WSProcessUpdateData } from 'shared/alerts/alerts.model';
import { BrandService } from 'shared/brand/brand.service';
import { Segment } from 'shared/segments-navigation/segment.interface';
import { SegmentsNavigationV2Component } from 'shared/segments-navigation/segments-navigation-v2/segments-navigation-v2.component';
import { formatDate } from 'utils/date';
import { Router } from '@angular/router';
import { SearchModel } from 'shared/search/search.model';
import { PusherService } from 'shared/pusher/pusher.service';

enum ShowContent {
  Stub = 'stub',
  ProcessingComplete = 'processingComplete',
}


@Component({
  moduleId: module.id,
  selector: 'comment-icon',
  templateUrl: 'comment-icon.component.html',
  styleUrls: ['comment-icon.component.scss'],
})
export class CommentIconComponent implements OnInit, OnDestroy {
  isActive: boolean = true;
  showNotification: boolean = false;
  showContent: ShowContent;
  alerts: Alert[];
  navigationSegments: Array<Segment> = [
    {
      id: 'all',
      title: 'All activities',
      active: true
    },
    // {
    //   id: 'deleted',
    //   title: 'Deleted',
    //   active: false
    // },
  ];
  searchModel: SearchModel;
  processesChannel: any;

  @Input() color: string = 'white'; // enum['white', 'blue']
  @Input() allowActiveMode: boolean = true;

  @ViewChild(SegmentsNavigationV2Component, {static: false})
  segmentsNavigation: SegmentsNavigationV2Component;

  constructor(
    private elementRef: ElementRef,
    private ref: ChangeDetectorRef,
    private alertsService: AlertsService,
    protected brandService: BrandService,
    protected router: Router,
    private ngZone: NgZone,
    private pusherService: PusherService,
    @Inject(DOCUMENT) private document: any,
  ) {}

  ngOnInit() {
    this.ngZone.runOutsideAngular(() => {
      fromEvent(this.document, 'click')
      .pipe(takeWhile(() => this.isActive))
      .subscribe((event: MouseEvent) => {
        this.onDocumentClick(event);
      });
    });

    this.alertsService.processingComplete
    .pipe(takeWhile(() => this.isActive))
    .subscribe((event: MouseEvent) => {
      this.showNotification = true;
      this.showContent = ShowContent.ProcessingComplete;
      this.ref.markForCheck();
    });

    this.alertsService.getStoredAlerts(this.brandId)
      .pipe(takeWhile(() => this.isActive))
      .subscribe((alerts: Alert[]) => {
        this.alerts = alerts;
      });

    this.initPusher();
  }

  ngOnDestroy() {
    this.isActive = false;
  }

  private initPusher() {
    const pusher = this.pusherService.getPusher();
    this.processesChannel = pusher.subscribe(`processes.${this.brandId}`);
    this.processesChannel.bind('ProcessUpdated', (data: WSProcessUpdateData) => {
      const item = data.process;
      const existingItem = this.alerts.find((alert: Alert) => {
        return alert._id === item._id;
      });
      if (existingItem) {
        this.alerts = this.alerts.map(i => i._id === item._id ? { ...i, ...item } : i);
      } else {
        this.alerts = [item, ...this.alerts];
      }
      this.ref.markForCheck();
    });
  }

  showAndHide() {
    this.alertsService.getAlerts(this.brandId)
      .pipe(takeWhile(() => this.isActive))
      .subscribe((alerts: Alert[]) => {
        this.alerts = alerts;
        this.showNotification = true;
        this.ref.markForCheck();
        setTimeout(() => {
          this.showNotification = false;
          this.ref.markForCheck();
        }, 6000);
      });
  }

  onDocumentClick(event: any): void {
    if (this.showNotification) {
      this.showNotification = false;
      if (this.showContent === ShowContent.ProcessingComplete) {
        this.showContent = ShowContent.Stub;
      }
    } else {
      const show: boolean = this.elementRef.nativeElement.contains(event.target);
      if (this.showNotification === show) { return; }
      this.showNotification = show;
    }
    this.ref.markForCheck();
  }

  onSegmentSelect(event: any): void {
  }

  onNavigationClick(event: MouseEvent) {
    event.stopPropagation();
  }

  onRefreshClick(event: MouseEvent) {
    this.alertsService.getAlerts(this.brandId)
      .pipe(takeWhile(() => this.isActive))
      .subscribe((alerts: Alert[]) => {
        this.alerts = alerts;
        this.ref.markForCheck();
      });
    event.stopPropagation();
  }

  onAlertItemClick(item: Alert, event: MouseEvent) {
    switch (item.type) {
      case 'IMPORT_UPLOADED':
      case 'ACCEPT_IMPORTED':
      case 'ACCEPT_IMPORTED_APPS':
        this.router.navigate(['/import-manager']);
        break;
      case 'ACCEPT_PRICESHEETS':
        this.router.navigate(['/global-brand-information/price-sheets']);
        break;
      case 'ACCEPT_MARKETCOPY':
        this.router.navigate(['/global-brand-information/marketing']);
        break;
      case 'UPDATE_ITEM_STATUS':
        break;
      case 'UPDATE_ITEM_STATUS_DRAFT':
        break;
      case 'UPDATE_ITEM_STATUS_REVIEW':
        break;
      case 'UPDATE_ITEM_STATUS_APPROVED':
        break;
      case 'PUBLISH_ITEMS':
        break;
      case 'UPDATE_ASSET_STATUS':
        this.router.navigate(['/digital-assets']);
        break;
      case 'UPDATE_PRICESHEET_STATUS':
        this.router.navigate(['/global-brand-information/price-sheets']);
        break;
      case 'UPDATE_MARKETCOPY_STATUS':
        this.router.navigate(['/global-brand-information/marketing']);
        break;
      case 'EXPORT':
      case 'EXPORT_RERUN':
      case 'EXPORT_PIES65':
      case 'EXPORT_PIES67':
      case 'EXPORT_PIES70':
      case 'EXPORT_PIES71':
      case 'EXPORT_PIES72':
      case 'EXPORT_ACES30':
      case 'EXPORT_ACES32':
      case 'EXPORT_ACES40':
      case 'EXPORT_ACES41':
      case 'EXPORT_ACES42':
      case 'EXPORT_ACES32_OREILLY':
        this.router.navigate(['/export-manager']);
        break;
      case 'EXPORT_ASSETS':
        this.router.navigate(['/digital-assets']);
        break;
      case 'BULK_UPDATE_ITEMS':
        break;
      case 'ADD_TO_DATA_LIST':
      case 'ADD_VCDB_FITMENTS_TO_ITEMS':
      case 'ADD_VCDB_FITMENTS_TO_ITEMS_VIA_LIST':
      case 'ADD_TO_FITMENTS_LIST_VIA_FITMENT_LIST':
      case 'ADD_TO_FITMENTS_LIST_VIA_ITEM_APPS':
      case 'REMOVE_FITMENTS_FROM_ITEM':
      case 'ADD_TO_ITEMS_VIA_FITMENT_LIST':
        this.router.navigate(['/fitments']);
        break;
      case 'DELETE_ITEM':
      case 'UNARCHIVE_ITEM':
        break;
      case 'COPY_PRODUCT_FITMENTS':
        this.router.navigate(['/fitments']);
    }

    this.showNotification = false;
    event.stopPropagation();
  }

  get brandId(): string {
    return this.brandService.getBrand();
  }

  get filteredAlerts(): Alert[] {
    const selectedSegment: string = this.navigationSegments.find((segment) => {
      return segment.active === true;
    })?.id;
    switch (selectedSegment) {
      case 'all':
        return this.alerts;
      case 'deleted':
        return [];      // not implemented yet
      default:
        return [];
    }
  }

  getItemDate(item: Alert): string {
    if (!item) { return ''; }
    return formatDate(item.updated_at);
  }

  getItemIconClass(item: Alert): string {
    if (!item) { return ''; }
    switch (item.type) {
      case 'IMPORT_UPLOADED':
      case 'ACCEPT_IMPORTED':
      case 'ACCEPT_IMPORTED_APPS':
      case 'ACCEPT_PRICESHEETS':
      case 'ACCEPT_MARKETCOPY':
      case 'UPDATE_ITEM_STATUS':
        return 'default';
      case 'UPDATE_ITEM_STATUS_DRAFT':
        return 'draft';
      case 'UPDATE_ITEM_STATUS_REVIEW':
        return 'review';
      case 'UPDATE_ITEM_STATUS_APPROVED':
        return 'approved';
      case 'PUBLISH_ITEMS':
        return 'published';
      case 'UPDATE_ASSET_STATUS':
        return 'dam';
      case 'UPDATE_PRICESHEET_STATUS':
      case 'UPDATE_MARKETCOPY_STATUS':
      case 'EXPORT':
      case 'EXPORT_RERUN':
      case 'EXPORT_PIES65':
      case 'EXPORT_PIES67':
      case 'EXPORT_PIES70':
      case 'EXPORT_PIES71':
      case 'EXPORT_PIES72':
      case 'EXPORT_ACES30':
      case 'EXPORT_ACES32':
      case 'EXPORT_ACES40':
      case 'EXPORT_ACES41':
      case 'EXPORT_ACES42':
      case 'EXPORT_ACES32_OREILLY':
        return 'default';
      case 'EXPORT_ASSETS':
        return 'dam';
      case 'BULK_UPDATE_ITEMS':
        return 'bulk';
      case 'ADD_TO_DATA_LIST':
      case 'ADD_VCDB_FITMENTS_TO_ITEMS':
      case 'ADD_VCDB_FITMENTS_TO_ITEMS_VIA_LIST':
      case 'ADD_TO_FITMENTS_LIST_VIA_FITMENT_LIST':
      case 'ADD_TO_FITMENTS_LIST_VIA_ITEM_APPS':
      case 'REMOVE_FITMENTS_FROM_ITEM':
      case 'ADD_TO_ITEMS_VIA_FITMENT_LIST':
      case 'DELETE_ITEM':
      case 'UNARCHIVE_ITEM':
      case 'COPY_PRODUCT_FITMENTS':
        return 'default';
    }
  }

  getItemStatusIconClass(item: Alert): string {
    if (!item) { return ''; }
    switch (item.status) {
      case ProcessStatus.STATUS_PROCESSING:
      case ProcessStatus.STATUS_STARTED:
        return 'status-processing';
      case ProcessStatus.STATUS_COMPLETED:
        return 'status-completed';
      case ProcessStatus.STATUS_ERROR:
        return 'status-error';
    }
  }

  getItemInProgress(item: Alert): boolean {
    return item.status === ProcessStatus.STATUS_PROCESSING || item.status === ProcessStatus.STATUS_STARTED;
  }
}
